import { cn } from '@/lib/utils';
import { Tooltip, TooltipTrigger } from '@/components/ui/tooltip';
import { EquipeGlassTooltipContent } from '@/components/help-center/EquipeGlassTooltipContent';
import type { StaffOnlinePresence } from '@/lib/staff-online-status';

const PRESENCE_COPY: Record<
  StaffOnlinePresence,
  { label: string; detail: string; dot: string; ring: string; glow: string }
> = {
  online: {
    label: 'Online',
    detail: 'Está online no xat agora.',
    dot: 'bg-emerald-400',
    ring: 'ring-emerald-300/90',
    glow: 'shadow-[0_0_10px_rgba(52,211,153,0.85)]',
  },
  offline: {
    label: 'Offline',
    detail: 'Está offline no xat agora.',
    dot: 'bg-red-500',
    ring: 'ring-red-300/90',
    glow: 'shadow-[0_0_10px_rgba(239,68,68,0.75)]',
  },
  // API "available" → Disponível (laranja); sem alteração de lógica/cores.
  unknown: {
    label: 'Disponível',
    detail: 'Pode receber mensagens mesmo quando está offline.',
    dot: 'bg-orange-400',
    ring: 'ring-orange-300/90',
    glow: 'shadow-[0_0_10px_rgba(251,146,60,0.7)]',
  },
};

type StaffOnlineBadgeProps = {
  presence: StaffOnlinePresence;
  className?: string;
};

/** Indicador no canto do avatar + tooltip especial da Equipe. */
export function StaffOnlineBadge({ presence, className }: StaffOnlineBadgeProps) {
  const copy = PRESENCE_COPY[presence];

  return (
    <Tooltip delayDuration={120}>
      <TooltipTrigger asChild>
        <button
          type="button"
          className={cn(
            'absolute bottom-0.5 right-0.5 z-20 flex h-3.5 w-3.5 items-center justify-center rounded-full',
            'bg-[#030023] outline-none transition-transform duration-200',
            'hover:scale-110 focus-visible:ring-2 focus-visible:ring-sky-400/60 focus-visible:ring-offset-1 focus-visible:ring-offset-[#030023]',
            className
          )}
          aria-label={copy.label}
        >
          <span
            className={cn(
              'relative block h-2.5 w-2.5 rounded-full ring-2',
              copy.dot,
              copy.ring,
              copy.glow
            )}
          >
            {presence === 'online' || presence === 'unknown' ? (
              <span
                className={cn(
                  'absolute inset-0 animate-ping rounded-full',
                  presence === 'online' ? 'bg-emerald-400/70' : 'bg-orange-400/60'
                )}
                aria-hidden
              />
            ) : null}
          </span>
        </button>
      </TooltipTrigger>
      <EquipeGlassTooltipContent>
        <div className="flex items-start gap-2.5">
          <span
            className={cn(
              'mt-0.5 h-2.5 w-2.5 shrink-0 rounded-full ring-2 ring-white/20',
              copy.dot,
              copy.glow
            )}
            aria-hidden
          />
          <div className="min-w-0">
            <p className="font-poppins text-[12px] font-semibold tracking-wide text-white">
              {copy.label}
            </p>
            <p className="mt-0.5 font-inter text-[11px] leading-snug text-[#AEB7D6]">
              {copy.detail}
            </p>
          </div>
        </div>
      </EquipeGlassTooltipContent>
    </Tooltip>
  );
}
